import React, { useContext, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTable, faFileAlt, faFolderOpen } from '@fortawesome/free-solid-svg-icons'
import Shield from 'assets/Shield/Gold/ThirdLineShieldGold.svg'
import UserContext from 'context/user-context'
import NewView from 'dashboard/new'
// Relative: `report-builder` has no path alias in vite.config.ts / jest.config.cjs.
import NewReportModal from '../report-builder/new-report-modal'

// The index used to open straight into "Select Dataset", which quietly assumed the thing being
// created was a View. Now that Views and Reports share one index, the first question is which of
// the two you want — the dataset picker is still there, one step later.
const CREATE_OPTIONS = [
  {
    key: 'view',
    icon: faTable,
    label: 'Create View',
    description: 'A filtered slice of one dataset — the charts, columns, and filters you choose.'
  },
  {
    key: 'report',
    icon: faFileAlt,
    label: 'Create Report',
    description: 'A branded, multi-dataset document the assistant assembles from your Views.',
    betaOnly: true
  }
]

// `folder` is the folder the index is currently showing, when it is one the user can file into.
// Whatever they create lands in it, and the chooser says so — creating inside a folder and finding
// the result at the top level is the surprise this avoids. `folderPath` is the full path, so a
// nested folder reads as "Audits / 2026 / Q1" rather than an ambiguous "Q1".
function CreateMenu({ size = 'md', folder = null, folderPath = '' }) {
  const user = useContext(UserContext)
  const [showChooser, setShowChooser] = useState(false)
  const [showNewView, setShowNewView] = useState(false)
  const [showNewReport, setShowNewReport] = useState(false)

  const options = CREATE_OPTIONS.filter((o) => !o.betaOnly || user?.is_beta)

  // With reports unavailable there is no choice left to make — skip the chooser entirely rather
  // than showing a dialog with one option in it.
  const handleCreateClick = () => {
    if (options.length === 1) {
      setShowNewView(true)
      return
    }
    setShowChooser(true)
  }

  const choose = (key) => {
    setShowChooser(false)
    if (key === 'view') setShowNewView(true)
    if (key === 'report') setShowNewReport(true)
  }

  return (
    <React.Fragment>
      <Button variant="primary" size={size} onClick={handleCreateClick}>
        <img src={Shield} style={{ width: 15 }} alt="ThirdLine Shield" /> Create
      </Button>

      <Modal show={showChooser} onHide={() => setShowChooser(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5">What do you want to create?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {folder && (
            <div className="text-muted small mb-3 d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faFolderOpen} />
              <span>
                Creating in <span className="fw-semibold">{folderPath || folder.name}</span>
              </span>
            </div>
          )}
          <div className="d-flex gap-2">
            {options.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => choose(option.key)}
                className="flex-fill text-start rounded-3 p-3 border bg-white"
                style={{ cursor: 'pointer' }}
              >
                <FontAwesomeIcon icon={option.icon} className="text-primary mb-2" size="lg" />
                <div className="fw-semibold">{option.label}</div>
                <div className="text-muted small mt-1">{option.description}</div>
              </button>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      <NewView show={showNewView} onHide={() => setShowNewView(false)} folderId={folder?.id || null} />
      <NewReportModal
        show={showNewReport}
        onHide={() => setShowNewReport(false)}
        folderId={folder?.id || null}
      />
    </React.Fragment>
  )
}

export default CreateMenu
